import type {CountryCode} from "libphonenumber-js";
import type {ExternalBulkContactInput} from "../../store/features/Contacts/contactsService";
import type {ContactImportParseResult} from "./contactImportTypes";
import {getBrowserCountryCode, normalizeImportedPhoneNumber} from "../../utilis/msisdnUtilis";

interface ParsedVcard {
    firstName: string;
    lastName: string;
    company: string;
    email?: string;
    phones: string[];
}

/**
 * Parse a vCard 2.1/3.0/4.0 text blob into a flat list of contacts. Handles:
 * - line folding (continuation lines starting with space/tab — RFC 5545/6350),
 * - property parameters (`TYPE=CELL`, `CHARSET=UTF-8`, …),
 * - common escape sequences (`\,`, `\;`, `\n`, `\\`),
 * - `FN`, `N`, `ORG`, `EMAIL`, `TEL`.
 *
 * Does NOT decode QUOTED-PRINTABLE (vCard 2.1 / Apple exports occasionally
 * use it). If you see mojibake, ask the user to re-export from Contacts.app
 * in vCard 3.0 / UTF-8.
 */
export function parseVcard(text: string): ParsedVcard[] {
    // Strip BOM.
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

    // Unfold: a line beginning with a space or tab is a continuation of the
    // previous logical line. Normalize all line endings first.
    const unfolded: string[] = [];
    const rawLines = text.replace(/\r\n?/g, "\n").split("\n");
    for (const line of rawLines) {
        if ((line.startsWith(" ") || line.startsWith("\t")) && unfolded.length > 0) {
            unfolded[unfolded.length - 1] += line.slice(1);
        } else {
            unfolded.push(line);
        }
    }

    const contacts: ParsedVcard[] = [];
    let current: ParsedVcard | null = null;

    for (const line of unfolded) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed.toUpperCase() === "BEGIN:VCARD") {
            current = {firstName: "", lastName: "", company: "", phones: []};
            continue;
        }
        if (trimmed.toUpperCase() === "END:VCARD") {
            if (current) contacts.push(current);
            current = null;
            continue;
        }
        if (!current) continue;

        // Split off the property header (name + params) from the value at the
        // first colon that isn't inside a quoted parameter.
        const colonIdx = findValueColon(trimmed);
        if (colonIdx < 0) continue;
        const header = trimmed.slice(0, colonIdx);
        const value = unescapeVcardValue(trimmed.slice(colonIdx + 1));

        const [propName] = header.split(";");
        const name = propName.toUpperCase();

        switch (name) {
            case "FN":
                if (!current.firstName && !current.lastName) {
                    // FN is the display name; split on first whitespace as a
                    // best-effort fallback when N is missing.
                    const parts = value.split(/\s+/);
                    current.firstName = parts[0] ?? "";
                    current.lastName = parts.slice(1).join(" ");
                }
                break;
            case "N": {
                // N:Family;Given;Additional;Prefix;Suffix
                const parts = value.split(";");
                current.lastName = (parts[0] ?? "").trim();
                current.firstName = (parts[1] ?? "").trim();
                break;
            }
            case "ORG":
                // ORG:Company;Department
                current.company = value.split(";")[0].trim();
                break;
            case "EMAIL":
                if (!current.email && value.trim()) current.email = value.trim();
                break;
            case "TEL":
                if (value.trim()) current.phones.push(value.trim());
                break;
        }
    }

    return contacts;
}

/**
 * Map parsed vCards into the backend's `ExternalBulkContactInput`.
 */
export function vcardToExternalContacts(
    text: string,
    ownerId: string,
    defaultCountry: CountryCode = getBrowserCountryCode(),
): ExternalBulkContactInput[] {
    return parseVcardContacts(text, ownerId, defaultCountry).contacts;
}

export function parseVcardContacts(
    text: string,
    ownerId: string,
    defaultCountry: CountryCode = getBrowserCountryCode(),
): ContactImportParseResult {
    const parsed = parseVcard(text);
    const contacts: ExternalBulkContactInput[] = [];
    const failures: ContactImportParseResult["failures"] = [];

    if (parsed.length === 0) {
        return {
            contacts,
            failures: [{
                id: "vcf-file",
                name: "vCard file",
                phoneNumbers: [],
                messages: ["The file does not contain any complete vCard contacts."],
            }],
        };
    }

    for (let index = 0; index < parsed.length; index++) {
        const c = parsed[index];
        const normalizedPhones = c.phones.map(raw => ({
            raw,
            normalized: normalizeImportedPhoneNumber(raw, defaultCountry),
        }));
        const invalidPhones = normalizedPhones
            .filter(phone => !phone.normalized)
            .map(phone => phone.raw);
        const phones = normalizedPhones
            .map(phone => phone.normalized)
            .filter((phone): phone is string => !!phone);
        const name = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.company || "Unnamed contact";
        const messages: string[] = [];

        if (c.phones.length === 0) {
            messages.push("No phone number was provided.");
        } else if (invalidPhones.length > 0) {
            messages.push(`Invalid phone number${invalidPhones.length === 1 ? "" : "s"}: ${invalidPhones.join(", ")}.`);
        }
        if (!c.firstName && !c.lastName && !c.company) {
            messages.push("A name or company is required.");
        }
        if (messages.length > 0) {
            failures.push({
                id: `vcf-contact-${index + 1}`,
                name,
                phoneNumbers: c.phones,
                messages,
            });
            continue;
        }

        contacts.push({
            ownerId,
            firstName: c.firstName || c.company || undefined,
            lastName: c.lastName || undefined,
            email: c.email,
            company: c.company || undefined,
            availability: "PRIVATE",
            phoneNumbers: phones.map((phoneNumber, i) => ({
                phoneNumber,
                main: i === 0,
            })),
            source: "WEB_VCARD_IMPORT",
            externalIdentifier: {
                id: hashContact(c.firstName, c.lastName, phones[0]),
                name: "web_vcard_import",
            },
        });
    }

    return {contacts, failures};
}

function findValueColon(line: string): number {
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') inQuotes = !inQuotes;
        else if (c === ":" && !inQuotes) return i;
    }
    return -1;
}

function unescapeVcardValue(value: string): string {
    return value
        .replace(/\\n/gi, "\n")
        .replace(/\\,/g, ",")
        .replace(/\\;/g, ";")
        .replace(/\\\\/g, "\\");
}

function hashContact(firstName: string, lastName: string, msisdn: string): string {
    const key = `${firstName.toLowerCase()}|${lastName.toLowerCase()}|${msisdn}`;
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return `vcf-${hash.toString(16)}`;
}
